import Link from "next/link";
import { FC } from "react";
import { ChevronRight } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GridTileImage } from "./grid/tile";
import AddToCartButton from "./add-to-cart-button";
import { NOT_IMAGE } from "@/lib/constants";

const cardVariants = cva(
  "group relative overflow-hidden rounded-[12px] flex flex-col gap-5",
  {
    variants: {
      variant: {
        default: "bg-background p-5 xl:h-[500px]",
        large: "bg-background-50 p-[30px] xl:h-[760px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const imageVariants = cva(
  "relative w-full rounded-[12px] overflow-hidden",
  {
    variants: {
      variant: {
        default: "aspect-square xl:aspect-auto xl:h-[410px]",
        large: "h-[300px] sm:h-[450px] xl:h-[633px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const nameVariants = cva(
  "font-oswald font-medium leading-[100%] tracking-[0.2px] uppercase text-neutral-500 line-clamp-2",
  {
    variants: {
      variant: {
        default: "text-[18px] xl:text-[20px]",
        large: "text-[24px] xl:text-[32px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface ProductCardProps extends VariantProps<typeof cardVariants> {
  imageUrl: string;
  product: {
    urlKey: string;
    name: string;
    id: string;
    type: string;
  };
}

export const ProductCard: FC<ProductCardProps> = ({
  imageUrl,
  product,
  variant,
}) => {
  // Determine the correct link based on product type
  const href = product.type === "category" 
    ? `/products?category=${product.urlKey}#products`
    : `/product-details/${product.urlKey}?type=${product.type}`;

  return (

      <div className={cn(cardVariants({ variant }))}>
        <Link
          href={href}
          aria-label={`View ${product.name} product details`}
          className="flex flex-col gap-5 h-full"
        >
          {/* Image */}
          <div className={cn(imageVariants({ variant }))}>
            <GridTileImage
              alt={product?.name || "Product image"}
              blurDataURL={NOT_IMAGE}
              className="rounded-[12px] object-contain object-center transition duration-300 ease-in-out group-hover:scale-105"
              placeholder="blur"
              loading="lazy"
              priority={false}
              fill
              src={imageUrl ?? NOT_IMAGE}
            />
          </div>

          {/* Name + Chevron */}
          <div className="flex items-center justify-between">
            <h2 className={cn(nameVariants({ variant }))}>
              {product?.name}
            </h2>
            <ChevronRight className="w-5 h-5 text-neutral-500" aria-hidden="true" />
          </div>
        </Link>

        {/* Add to cart hover - only show for products, not categories */}
        {product.type !== "category" && (
          <div className="absolute bottom-20 left-1/2 flex -translate-x-1/2 items-center rounded-full border-[1.5px] border-white bg-white/70 px-4 py-1.5 text-xs font-semibold text-black opacity-0 shadow-2xl backdrop-blur-md duration-300 group-hover:opacity-100">
            <AddToCartButton
              productType={product.type}
              urlKey={product.urlKey}
              productId={product.id}
            />
          </div>
        )}
      </div>

  );
};